import React, { createContext, useContext, useState } from 'react';

import { CoachingSessionGQL as CoachingSessionType } from 'coaching/containers/GetCoachingSessionsAndRecordingsContainer';
import { CoachingUserType } from 'globalTypes';
import { formatDateForCoordinator, formatTimeForCoordinator } from 'lib/dateTimeFormat';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import Text from 'components/Text';

export type CoachingSessionProps = {
  session: CoachingSessionType;
  participantUuid: string;
  userName: string;
  userType: CoachingUserType;
};

export type ConfirmDeleteDialogType = {
  ongoingSeries: boolean;
  coachingSessionInstanceUuid?: string;
  instanceStartDateTime?: string;
  instanceDateString?: string;
  cancellationReason?: string;
};

export type CoachingSessionContextType = {
  confirmDeleteDialog: ConfirmDeleteDialogType | undefined;
  setConfirmDeleteDialog: (val: ConfirmDeleteDialogType | undefined) => void;
  isActuallyLateCancellation: boolean | undefined;
  setIsActuallyLateCancellation: (val: boolean | undefined) => void;
};

export const CoachingSessionContext = createContext<CoachingSessionContextType>({
  confirmDeleteDialog: undefined,
  setConfirmDeleteDialog: (_: ConfirmDeleteDialogType | undefined) => {},
  isActuallyLateCancellation: undefined,
  setIsActuallyLateCancellation: (_: boolean | undefined) => {},
});

export const useCoachingSessionState = () => useContext(CoachingSessionContext);

export const CoachingSessionContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [confirmDeleteDialog, setConfirmDeleteDialog] = useState<
    ConfirmDeleteDialogType | undefined
  >(undefined);
  const [isActuallyLateCancellation, setIsActuallyLateCancellation] = useState<boolean | undefined>(
    undefined
  );
  const contextState = {
    confirmDeleteDialog,
    setConfirmDeleteDialog,
    isActuallyLateCancellation,
    setIsActuallyLateCancellation,
  };
  return (
    <CoachingSessionContext.Provider value={contextState}>
      {children}
    </CoachingSessionContext.Provider>
  );
};

export const SessionRowHeader = ({
  session,
  instanceDateString,
}: {
  session: CoachingSessionType;
  instanceDateString?: string;
}) => {
  const headerText =
    instanceDateString ??
    (session.instances.length === 1
      ? formatDateForCoordinator(instanceDateString ?? session.startDateTime, 'ddd, MMM D')
      : 'Ongoing');
  return (
    <Row size="xxsmall">
      <Text truncate size="small">
        <MediaObject center gutter="small">
          <Text>{headerText}</Text>
          <Text color="graphite">{session.coach.name}</Text>
        </MediaObject>
      </Text>
    </Row>
  );
};

export const SessionRowMetadata = ({ session }: { session: CoachingSessionType }) => {
  // if the `uuid` of the session schedule is the same as the `uuid` of the first instance,
  // it means that we are programatically creating that first instance so that it shows
  // up, despite the fact that no instances have actually been materialized yet.
  const scheduleWithoutInstancesText = session.instances[0].eventId === session.uuid && (
    <Text size="xxsmall" color="graphite">
      If this is a recurring schedule, instances will show up within 60 days of the start date.
    </Text>
  );
  const startTime = formatTimeForCoordinator(session.startDateTime);

  return (
    <>
      <Row size="xxxsmall">
        <Text size="small" element="div">{`${startTime} • ${session.duration} min `}</Text>
      </Row>
      {scheduleWithoutInstancesText}
    </>
  );
};
